import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './PerinHealthGrid.module.css';
import { setSelectAll, setSelectedRows, setSelectedHeaders } from './PerinHealthGridSlice';
import { BlueProfileIcon, SortingDownArrowIcon, SortingUpArrowIcon, DefaultHeaderFilterIcon, ActiveHeaderFilterIcon } from './icons/Icons';
import { Button, Tooltip, OverlayTrigger, Dropdown } from 'react-bootstrap';
import EllipsesIcon from './icons/EllipsesIcon';
import PerinHealthGridResizable from './PerinHealthGridResizable';
import { Paginator } from 'primereact/paginator';
import 'primereact/resources/primereact.min.css';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import CustomDropdownToggle from './CustomDropdownToggle';
import Logger from '../services/Logger';
import { LogElements, LogAction } from '../services/LogElements';

const PerinHealthGrid = (props) => {
  const {
    headers,
    dataList,
    showCheckbox = false,
    itemsPerPage = 15,
    iconMapping = [],
    showEditIcon = false,
    accounts = false,
    onRowClick,
    moreEditOptions = false,
    showHeaderFilter = false,
    showProfilePicture = false,
    rowValueAction,
    onRowValueClick, 
    useChips = false,
    showBadge,
    showStatusBars = false,
    inpatientFilters = false
  } = props;

  const [hoveredRow, setHoveredRow] = useState(null);
  const [columnWidths, setColumnWidths] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [rowPerPage, setItemsPerPage] = useState(itemsPerPage);
  const [headerFilterOpen, setHeaderFilterOpen] = useState(false);
  const moreEditOptionsButtonRef = useRef(null);
  const allButtonRef = useRef(null);
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(itemsPerPage);

  const [expandedItems, setExpandedItems] = useState({});

  const toggleItemExpansion = useCallback((rowId) => {
    setExpandedItems(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  }, []);

  const dispatch = useDispatch();
  const filter = useSelector((state) => state.filters);

  const perinHealthGrid = useSelector((state) => state.perinHealthGrid);
  const { selectedRows, selectAll, selectedHeaders, selectedItem } = perinHealthGrid;

  useEffect(() => {
    setSelectedRows([]);
  }, [dataList]);

  useEffect(() => {
    dispatch(setSelectedHeaders(headers));
  }, []);

  const sanitizeRow = (row) => {
    const cleanRow = { ...row };
  
    // Drop/replace any React elements or non-serializable values
    Object.keys(cleanRow).forEach((key) => {
      const value = cleanRow[key];
      if (typeof value === 'function' || React.isValidElement(value)) {
        cleanRow[key] = null; // or string placeholder, or omit if you prefer
      }
    });
  
    return cleanRow;
  };
  const sanitizeRows = (rows) => rows.map(sanitizeRow);

  const filteredData = useMemo(() => {
    if (!Array.isArray(dataList) || dataList.length === 0) {
      return [];
    }

    if (!headers || headers.length === 0) {
      return dataList;
    }

    return dataList.filter((item) => {
      const matchesSearch = headers.some((header) => {
        const headerValue = item[header.key];
        return String(headerValue).toLowerCase().includes(filter.searchText.toLowerCase());
      });

      if (accounts) {
        const matchesOrganization = filter.selectedOrganization === '' || item['organizationIds']?.includes((Number(filter.selectedOrganization)));
        const matchesGroup = filter.selectedGroup === '' || item['groupIds']?.includes(Number(filter.selectedGroup)) || item['groupIds']?.length === 0;
        const matchesLocation = filter.selectedLocation === '' || item['subGroupIds']?.includes(Number(filter.selectedLocation)) || item['subGroupIds']?.length === 0;
        const matchesTeam = filter.selectedTeam === '' || item['teamIds']?.includes(Number(filter.selectedTeam)) || item['teamIds']?.length === 0;;
        return matchesOrganization && matchesSearch && matchesGroup && matchesLocation && matchesTeam;
      } else if (inpatientFilters) {
        const matchesOrganization = filter.selectedOrganization === "" || item['organizationId'] === Number(filter.selectedOrganization);
        const matchesLocation = filter.selectedInpatientLocation === "" || item['location'] === filter.selectedInpatientLocation;
        const matchesFloor = filter.selectedInpatientFloor === "" || item['floor'] === filter.selectedInpatientFloor;
        const matchesRoom = filter.selectedInpatientRoom === "" || item['room'] === filter.selectedInpatientRoom;
        return matchesSearch && matchesOrganization && matchesLocation && matchesFloor && matchesRoom;
      } else if (filter.selectedOrganization !== '') {
        const organizationHeaderValue = item['organizationId'];
        const organization = filter.selectedOrganization === 'All' || String(organizationHeaderValue).toLowerCase().startsWith(String(filter.selectedOrganization).toLowerCase());

        if (filter.selectedGroup !== '') {
          const groupHeaderValue = item['groupId'];
          const group = filter.selectedGroup === 'All' || String(groupHeaderValue).toLowerCase().startsWith(String(filter.selectedGroup).toLowerCase());
          return group && matchesSearch;
        } else {
          return organization && matchesSearch;
        }
      } else {
        return matchesSearch;
      }
    });
  }, [dataList, filter, headers, accounts]);

  const sortKeys = {
    spo2: 'spo2_sort',
    hr: 'hr_sort',
    temp: 'temp_sort',
    weight: 'weight_sort',
    ausc: 'ausc_sort',
    ecgHrv: 'ecgHrv_sort',
    rr: 'rr_sort',
    bp: 'bp_sort',
    device_status: 'device_status_sort',
    alerts: 'alerts_sort',
    out_of_limit: 'outOfLimit_sort',
    enrollment: 'enrollment_sort',
    patient_severity: 'patient_severity_sort',
    formattedCreatedDate: 'createdDate'
  };

  const handleSortingChange = (key, direction) => {
    if (sortField === key && sortOrder === direction) {
      // reset rows
      setSortField("");
      setSortOrder("asc");
    } else {
      setSortField(key);
      setSortOrder(direction);
    }
  };

  const sortedData = useMemo(() => {
    if (sortField) {
      let sortKey = sortKeys[sortField] ?? sortField;
      const sorted = [...filteredData].sort((a, b) => {
        if (!a[sortKey] && a[sortKey] !== "") return 1;
        if (!b[sortKey] && b[sortKey] !== "") return -1;
        if (!a[sortKey] && !b[sortKey]) return 0;
        return (
          a[sortKey].toString().localeCompare(b[sortKey].toString(), "en", {
            numeric: true,
          }) * (sortOrder === "asc" ? 1 : -1)
        );
      });
      return sorted;
    } else {
      // Default sorting: out_of_limit first, then enrollment
      const sorted = [...filteredData].sort((a, b) => {
        const outOfLimitComparison = b.outOfLimit_sort - a.outOfLimit_sort;  
        if (outOfLimitComparison !== 0) return outOfLimitComparison;

        const enrollmentComparison = b.enrollment_sort - a.enrollment_sort;  
        if (enrollmentComparison !== 0) return enrollmentComparison;

        return 0;
      });

    return sorted;
      }
    }, [sortField, sortOrder, filteredData]);

  const handleHeaderFilterSelection = (header) => {
    const index = selectedHeaders.findIndex((selectedHeader) => selectedHeader.label === header.label);
    if (index !== -1) {
      if (selectedHeaders.length === 1) {
        return;
      }
      const updatedHeaders = [...selectedHeaders];
      updatedHeaders.splice(index, 1);
      dispatch(setSelectedHeaders(updatedHeaders));
    } else {
      dispatch(setSelectedHeaders([...selectedHeaders, header]));
    }
  };

  const handleHeaderCheckboxChange = () => {
    const allItems = filteredData;
    dispatch(setSelectAll(!selectAll));
    dispatch(setSelectedRows(!selectAll ? sanitizeRows(allItems) : []));
  };

  const handleRowCheckboxChange = (item) => {
    const updatedSelectedRows = [...selectedRows];
    const index = updatedSelectedRows.findIndex((selectedItem) => selectedItem.id === item.id);

    if (index !== -1) {
      updatedSelectedRows.splice(index, 1);
    } else {
      updatedSelectedRows.push(sanitizeRow(item));
    }

    dispatch(setSelectedRows(updatedSelectedRows));
    dispatch(setSelectAll(updatedSelectedRows.length === filteredData.length));
  };

  const onEditClickHandler = (item) => {
    if (onRowClick) {
      onRowClick(item);
    }
  };

  const createTooltipChip = (item) => {
    let chipTooltip;
    let overlayLabel;
    if (item.length === 0) {
      return;
    } else if (item.length === 1) {
      if (item[0].length > 15) {
        chipTooltip = (
          <Tooltip id="tooltip">
            {item[0]}
          </Tooltip>
        );
        overlayLabel = `${item[0].substring(0, 11)}...`;
      } else {
        return (
          <div className={styles['chip-container']}>
            {item[0]}
          </div>
        );
      }
    } else {
      chipTooltip = (
        <Tooltip id="tooltip">
          {item.map((arrayItem, arrayIndex) => (
            <React.Fragment key={arrayIndex}>
              {arrayItem}
              {arrayIndex < item.length - 1 && <br />}
            </React.Fragment>
          ))}
        </Tooltip>
      );
      overlayLabel = `${item[0].length > 9 ? `${item[0].substring(0, 8)}...` : item[0]} ${item.length >= 2 ? `+${item.length - 1}` : ""}`;
    }
    return (
      <OverlayTrigger placement="bottom" overlay={chipTooltip}>
        <div style={{ cursor: "pointer" }} className={styles['chip-container']}>
          {overlayLabel}
        </div>
      </OverlayTrigger>
    );
  };

  const handleRowValueClick = (value, item) => {
    if (value !== rowValueAction || !rowValueAction) {
      return;
    } else {
      onRowValueClick(item);
    }
  };

  const getBackgroundColor = (key, item) => {
    if (React.isValidElement(item[key])) {
      const { props } = item[key];

      // Check and return the data-bgcolor prop if it exists
      return props['data-bgcolor'] || null;
    }
  };

  const checkForBadge = (item) => {
    if (!rowValueAction) {
      return;
    } else {
      return (
        <td>{showBadge(item)}</td>
      );
    }
  };

  const onPageChange = (event) => {
    setFirst(event.first);
    setRows(event.rows);
  };

  const currentItems = useMemo(() => {
    return sortedData.slice(first, first + rows);
  }, [sortedData, first, rows]);

  return (
    <div style={{ display: "flex", flexDirection: "column", flexGrow: 1, height:0 }}>
      <div style={{flexGrow: 1, height:"100", overflow:"auto" }}>
        <table className={styles['data-table']}>
          <thead>
            <tr style={{ borderBottom: "1px solid #ddd" }}>
              {showStatusBars && (
                <th style={{ width: '4px'}}>
                </th>
              )}
              {showCheckbox && (
                <th style={{ width: '10px' }}>
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleHeaderCheckboxChange}
                  />
                </th>
              )}
              {headers.map((header, index) => (
                selectedHeaders.findIndex((selectedHeader) => selectedHeader.label === header.label) !== -1 &&
                (
                  <PerinHealthGridResizable key={index}>
                    {({ ref, styles }) => (
                      <th style={{ width: columnWidths[index] || header.width}}>
                        <div className={styles['resizable-column']} data-index={index} ref={ref}>
                          {header.label}
                            <div className={styles['sorting-icon']}>
                              <div onClick={() => handleSortingChange(header.key, "asc")}>
                                <SortingUpArrowIcon color={(sortField === header.key && sortOrder === "asc") ? "#686869" : "#B9B9B9"} />
                              </div>
                              <div onClick={() => handleSortingChange(header.key, "desc")}>
                                <SortingDownArrowIcon color={(sortField === header.key && sortOrder === "desc") ? "#686869" : "#B9B9B9"} />
                              </div>
                            </div>
                          {index !== headers.length - 1 && (
                            <div className={styles['resizer']}></div>
                          )}
                        </div>
                      </th>
                    )}
                  </PerinHealthGridResizable>
                )
              ))}
              {showEditIcon && <th style={{ width: '20px' }}></th>}
              {showHeaderFilter &&
                <th style={{ width: '5px' }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center" }} onClick={() => setHeaderFilterOpen((prev) => !prev)}>
                      {headerFilterOpen ?
                        <ActiveHeaderFilterIcon />
                        :
                        <DefaultHeaderFilterIcon />
                      }
                    </div>
                    {headerFilterOpen &&
                      <div className={styles.HeaderFilterMenu}>
                        {headers.map((header, index) => (
                          <label className={styles.HeaderFilterItem} key={index}>
                            <input type="checkbox"
                              checked={selectedHeaders.findIndex((selectedHeader) => selectedHeader.label === header.label) !== -1}
                              onChange={() => handleHeaderFilterSelection(header)}
                              style={{ marginRight: "5px" }} />
                            {header.label}
                          </label>
                        ))}
                      </div>
                    }
                  </div>
                </th>}
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 && (
              <tr>
                <td colSpan={headers.length + (showCheckbox ? 1 : 0) + (showEditIcon ? 1 : 0)}>
                  <div className={styles.empty}>
                    No content found for this user
                  </div>
                </td>
              </tr>
            )}
            {currentItems.map((item, rowIndex) => (
              <tr
                key={`row-${rowIndex}`} // Use rowIndex to ensure each row has a unique key
                className={`${hoveredRow === rowIndex ? styles['hovered-row'] : ''} ${selectedRows.some(row => row.id === rowIndex) ? styles['selected-row'] : ''}`}
                onMouseEnter={() => setHoveredRow(rowIndex)}
                onMouseLeave={() => setHoveredRow(null)}
              >
                {showStatusBars && (
                  <td style={{ width: '4px', background:item["bar_status"] ? "#2A7F38":"#CA3B3B" }}>
                </td>
              )}
                {showCheckbox && (
                  <td style={{ width: '20px' }}>
                    <input
                      type="checkbox"
                      checked={selectedRows.findIndex((selectedItem) => selectedItem.id === item.id) !== -1}
                      onChange={() => handleRowCheckboxChange(item)}
                    />
                  </td>
                )}
                {headers.map((header, colIndex) => (
                  selectedHeaders.findIndex((selectedHeader) => selectedHeader.label === header.label) !== -1 &&
                  <td key={`cell-${rowIndex}-${colIndex}`} className={styles['grid-cell']} style={{ width: columnWidths[colIndex] || header.width, backgroundColor: getBackgroundColor(header.key, item), 'borderRight': getBackgroundColor(header.key, item) === null ? 'none' : '1px solid white' }}>
                    {iconMapping[item[header.key]] ? (
                      <div className={styles['icon-container']}>
                        {iconMapping[item[header.key]].icon}
                        {iconMapping[item[header.key]].text && (
                          <span>{iconMapping[item[header.key]].text}</span>
                        )}
                      </div>
                    ) : Array.isArray(item[header.key]) ? (
                      useChips ? (
                        // if header is an array and useing chips, show first item as a chip
                        createTooltipChip(item[header.key])
                      ) : (
                        // if header is an array, show each item on a new line
                        (() => {
                          const isExpanded = expandedItems[item.id];
                          const itemsToShow = isExpanded ? item[header.key] : item[header.key].slice(0, 2);
                          const hasMore = item[header.key].length > 2;
                          return (
                            <>
                              {itemsToShow.map((arrayItem, arrayIndex) => (
                                <React.Fragment key={`arrayItem-${rowIndex}-${colIndex}-${arrayIndex}`}>
                                  {arrayItem}
                                  {arrayIndex < itemsToShow.length - 1 && <br />}
                                </React.Fragment>
                              ))}
                              {hasMore && !isExpanded && (
                                <div 
                                  className={styles.expandButton}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleItemExpansion(item.id);
                                  }}
                                >
                                  ...
                                </div>
                              )}
                              {hasMore && isExpanded && (
                                <div 
                                  className={styles.showLessButton}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleItemExpansion(item.id);
                                  }}
                                >
                                  Show less
                                </div>
                              )}
                            </>
                          );
                        })()
                      )
                    ) : showProfilePicture === header.key || rowValueAction === header.key ? (
                      <div className={styles['icon-container']}>
                        {/* show profile photo icon next to header value */}
                        {showProfilePicture === header.key &&
                          <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
                            {item.profilePictureURL ? (
                              <img
                                src={item.profilePictureURL}
                                alt={`${item.firstName} ${item.lastName}`}
                                style={{
                                  width: '28px',
                                  height: '28px',
                                  borderRadius: '50%',
                                  border: '2px solid grey',
                                  objectFit: 'cover'
                                }}
                              />
                            ) : (
                              <>
                                {item.firstName && item.lastName && 
                                  <span className={styles.GridInitialsIcon}>
                                    {`${item.firstName[0]}${item.lastName[0]}`}
                                  </span>
                                }
                                <BlueProfileIcon width='28' height='28' />
                              </>
                            )}
                          </div>
                        }
                        <div className={rowValueAction === header.key ? styles['row-value-link'] : ''} onClick={() => handleRowValueClick(header.key, item)}>
                          {item[header.key]}
                        </div>
                      </div>
                    ) : (
                      item[header.key]
                    )}
                  </td>
                ))}
                {showEditIcon && (moreEditOptions ?
                    <td style={{ width: '90px', position: "relative" }}>
                      <Dropdown>
                          <Dropdown.Toggle as={CustomDropdownToggle} id="custom-grid-dropdown">
                              <EllipsesIcon />
                          </Dropdown.Toggle>
                          <Dropdown.Menu className="custom-bootstrap-menu">
                              {(typeof moreEditOptions === 'function' ? moreEditOptions(item) : moreEditOptions).map((option, index) => (
                                <Dropdown.Item key={index} onClick={() => onEditClickHandler({type: option.type, item})}>
                                  {option.icon} {option.label}
                                </Dropdown.Item>
                              ))}
                          </Dropdown.Menu>
                      </Dropdown>
                      </td>

                  :
                  <td style={{ width: '90px', cursor:"pointer"}}> {selectedRows.length <= 0 && showEditIcon && hoveredRow === rowIndex && <button className={styles['update-button']} disabled={selectedRows.length >= 1} onClick={() => onEditClickHandler(item)}>Update</button>}</td>
                )}
                {showBadge &&
                  checkForBadge(item)
                }
                {showHeaderFilter &&
                  <td style={{ width: '5px' }}></td>
                }
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={styles.container}>
        <span style={{ 
          fontFamily: 'Roboto',
          fontSize: '12px',
          fontWeight: 500,
          lineHeight: '16px',
          textAlign: 'left',
          color: '#25282B'
        }}>Total Number: {filteredData.length}</span>
        <Paginator
          first={first}
          rows={rows}
          totalRecords={filteredData.length}
          onPageChange={onPageChange}
          rowsPerPageOptions={[15, 25, 50, 100]}
          template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown "
          className={styles.customPaginator}
        />
        <div></div>
      </div>
    </div>
  );
};

export default PerinHealthGrid;
